// https://theme-ui.com/theme-spec

/* Misc Notes:
  1. heading refers to h1, h2, h3, etc. Not the header/footer
  10. This file is executed once Serverside, and once Clientside
*/

/////    Brand Specific Color Settings    /////

const havenPrimaryPalette = {

  primaries: {
    white: '#fff',
    black: '#000',
    beige: '#F9F4F0',
    cream: '#F5F1E8',
    taupe: '#CBBAB0',
    rose: '#D7A8A0',
    darkBrown: '#333333',
  },

  backgrounds: {
    white: '#fff',
    black: '#000',
  },

  fonts: {
    lato: 'Lato, Helvetica, Arial, sans-serif',
    latoLight: 'Lato-Light, Helvetica, Arial, sans-serif',
    playfair: 'Playfair, Helvetica, Arial, sans-serif',
    playfairItalic: 'PlayfairItalic, Helvetica, Arial, sans-serif',
    menlo: 'Menlo, monospace',
  },
  referenceColors: {
    text: '#333333', 
    body: '#333333', 
    heading: '#333333',
    background: '#E8DCCF', 
    primary: '#D7A8A0',
    secondary: '#CBBAB0', 
    muted: '#CBBAB0', 
    navList: '#333333', 
    headerCTA: '#D7A8A0', 
    link: '#D7A8A0', 
  }
};

// Theme config exported for access in components, build loop, and builder configs
const havenStyles = {
  referenceColors: havenPrimaryPalette.referenceColors,
  breakpoints: ['48em', '64em', '90em'],
  space: {
    none: '0',
    small: ['0.375em', '0.375em', '0.75em'], // Arrays set responsive values from smallest breakpoint to largest
    medium: ['0.5em', '0.5em', '1em'],
    large: ['0.75em', '0.75em', '1.5em'],
    xlarge: ['1.2em', '1.5em', '3em'],
    mega: ['4em', '4em', '8em'],
  },
  sizes: {
    container: ['100%', '43.25em', '61.25em', '76.25em'],
    viewHeight100: '100vh',
    viewWidth100: '100vw',
    navHeight: '75px',
    footerHeight: '250px',
  },
  fonts: {
    body: havenPrimaryPalette.fonts.lato,
    heading: havenPrimaryPalette.fonts.lato,
    playfair: havenPrimaryPalette.fonts.playfair,
    monospace: havenPrimaryPalette.fonts.menlo,
    nav: havenPrimaryPalette.fonts.lato,
  },
  fontSizes: [16, 18, 24, 19, 30, 24, 36, 36, 48, 32, 48],
  fontWeights: {
    body: 400,
    heading: 400,
    bold: 600,
  },
  lineHeights: {
    body: 1.75,
    heading: 1.25,
    button: 1.5,
  },
  letterSpacings: {
    body: 0,
    heading: 0,
    caps: '0.05em',
    caption: '0.02em',
  },
  // User exposed color options in the Builder UI are also drawn from this theme
  // Color must be defined here to be used in backgroundColors and other sub categories
  colors: { 
    text: havenPrimaryPalette.referenceColors.text,
    body: havenPrimaryPalette.referenceColors.body,
    heading: havenPrimaryPalette.referenceColors.heading,
    primary: havenPrimaryPalette.primaries.black,
    secondary: havenPrimaryPalette.primaries.darkBrown,
    muted: havenPrimaryPalette.primaries.taupe,
    link: havenPrimaryPalette.primaries.rose,

    beige: havenPrimaryPalette.primaries.beige,
    cream: havenPrimaryPalette.primaries.cream,
    taupe: havenPrimaryPalette.primaries.taupe,
    brown: havenPrimaryPalette.primaries.rose,
    darkBrown: havenPrimaryPalette.primaries.darkBrown,
    
    white: havenPrimaryPalette.primaries.white,
    black: havenPrimaryPalette.primaries.black,
    offwhite: havenPrimaryPalette.primaries.beige,
    
    transparent: 'transparent',


  },
  // Used for component background colors
  backgroundColors: {
    beige: havenPrimaryPalette.primaries.beige,
    cream: havenPrimaryPalette.primaries.cream,
    taupe: havenPrimaryPalette.primaries.taupe,
    brown: havenPrimaryPalette.primaries.rose,
    white: havenPrimaryPalette.primaries.white,
    black: havenPrimaryPalette.primaries.black,
    transparent: 'transparent',
  },
  radii: {
    card: ['0.25em', '0.5em'],
    button: '0',
  },
  shadows: {
    card: '0 0.125em 0.1875em 0 rgba(0,0,0,0.2)',
  },
  text: {
    body: {
      fontFamily: 'body',
      fontWeight: 'body',
      lineHeight: 'body',
      letterSpacing: 'body',
    },
    heading: {
      fontFamily: 'heading',
      fontWeight: 'heading',
      lineHeight: 'heading',
      letterSpacing: 'heading',
    },
  },
  // theme variants
  buttons: {
    primary: {
      appearance: 'none',
      alignItems: 'center',
      bg: '#333333',
      border: 0,
      borderRadius: '0',
      color: '#fff',
      display: 'inline-flex',
      fontFamily: havenPrimaryPalette.fonts.lato,
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      justifyContent: 'center',
      lineHeight: 'button',
      letterSpacing: '0.05em',
      marginBottom: 'medium',
      padding: ['0.875em 1.75em', '0.875em 2em'],
      position: 'relative',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
      transition: 'background-color 0.3s ease',
      cursor: 'pointer',
      textTransform: 'uppercase',
      
      '&:hover': {
        bg: '#D7A8A0',
        transition: 'background-color 0.3s ease',
      },
      '&:focus': {
        outline: 'none',
        boxShadow: '0 0 0 3px rgba(51, 51, 51, 0.3)',
      },
      '& + a': {
        marginLeft: ['none', 'none', 'small'],
      },
    },
    rose: {
      variant: 'buttons.primary',
      bg: '#D7A8A0',
      color: '#fff',
      border: 0,
      '&:hover': {
        bg: '#333333',
        transition: 'background-color 0.3s ease',
      },
      '&:focus': {
        outline: 'none',
        boxShadow: '0 0 0 3px rgba(215, 168, 160, 0.3)',
      },
    },
    beige: {
      variant: 'buttons.primary',
      bg: '#E8DCCF',
      color: '#333333',
      border: '1px solid #CBBAB0',
      '&:hover': {
        bg: '#CBBAB0',
        borderColor: '#CBBAB0',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      },
      '&:focus': {
        outline: 'none',
        boxShadow: '0 0 0 3px rgba(51, 51, 51, 0.2)',
      },
    },
    blackBorder: {
      variant: 'buttons.primary',
      bg: 'transparent',
      color: '#000',
      border: '1px solid #333333',
      '&:hover': {
        bg: '#E8DCCF',
        borderColor: '#333333',
        transition: 'background-color 0.3s ease',
      },
      '&:focus': {
        outline: 'none',
        boxShadow: '0 0 0 3px rgba(0, 0, 0, 0.2)',
      },
    },
    link: {
      variant: 'buttons.primary',
      paddingLeft: 0,
      paddingRight: 0,
      bg: 'transparent',
      color: '#333333',
      border: 'none',
      textDecoration: 'underline',
      '&:hover': {
        bg: 'transparent',
        color: '#D7A8A0',
      },
    },
    darkBrown: {
      variant: 'buttons.primary',
      bg: '#333333',
      color: '#fff',
      border: 0,
      '&:hover': {
        bg: '#D7A8A0',
        transition: 'background-color 0.3s ease',
      },
      '&:focus': {
        outline: 'none',
        boxShadow: '0 0 0 3px rgba(51, 51, 51, 0.3)',
      },
    },
  },
  // markdown styles
  styles: {
    root: {
      header: {
        fontSize: '11px',
      },
      variant: 'text.body',
      h1: {
        variant: 'text.heading',
        fontWeight: '200',
        fontSize: ['32px', '48px'],
        fontFamily: havenPrimaryPalette.fonts.playfair, 
        lineHeight: '1.2',
        letterSpacing: '0',
        textTransform: 'none',
        margin: '0 0 1rem 0',
      },
      h2: {
        variant: 'text.heading',
        fontSize: ['28px', '36px'],
        fontFamily: havenPrimaryPalette.fonts.playfair, 
        fontWeight: '200',
        lineHeight: '1.3',
        letterSpacing: '0',
        margin: '0 0 0.75rem 0',
      },
      h3: {
        variant: 'text.heading',
        fontSize: ['24px', '30px'],
        fontFamily: havenPrimaryPalette.fonts.lato,
        fontWeight: '500',
        lineHeight: '1.4',
        letterSpacing: '0',
        margin: '0 0 0.5rem 0',
      },
      h4: {
        variant: 'text.heading',
        fontSize: ['22px', '28px'],
        fontFamily: havenPrimaryPalette.fonts.lato,
        lineHeight: '1.4',
        letterSpacing: '0',
        fontWeight: '500',
        margin: '0 0 0.5rem 0',
      },
      h5: {
        variant: 'text.heading',
        fontSize: ['18px', '22px'],
        fontFamily: havenPrimaryPalette.fonts.lato,
        lineHeight: '1.5',
        fontWeight: '600',
        letterSpacing: '0',
        margin: '0 0 0.5rem 0',
      },
      h6: {
        variant: 'text.heading',
        fontSize: ['16px', '18px'],
        fontFamily: havenPrimaryPalette.fonts.lato,
        fontWeight: '500',
        lineHeight: '1.5',
        letterSpacing: '0',
        margin: '0 0 0.5rem 0',
        textTransform: 'none'
      },
      // Utility classes to apply heading styles to any element
      // Usage: <h2 class="header-h5"> will be styled as h5
      '.header-h1': {
        variant: 'text.heading',
        fontWeight: '300',
        fontSize: ['32px', '48px'],
        fontFamily: havenPrimaryPalette.fonts.playfair, 
        lineHeight: '1.2',
        letterSpacing: '0',
        textTransform: 'none',
        margin: '0 0 1rem 0',
      },
      '.header-h2': {
        variant: 'text.heading',
        fontSize: ['28px', '36px'],
        fontFamily: havenPrimaryPalette.fonts.playfair, 
        fontWeight: '400',
        lineHeight: '1.3',
        letterSpacing: '0',
        margin: '0 0 0.75rem 0',
      },
      '.header-h3': {
        variant: 'text.heading',
        fontSize: ['24px', '30px'],
        fontFamily: havenPrimaryPalette.fonts.lato,
        fontWeight: '400',
        lineHeight: '1.4',
        letterSpacing: '0',
        margin: '0 0 0.5rem 0',
      },
      '.header-h4': {
        variant: 'text.heading',
        fontSize: ['22px', '28px'],
        fontFamily: havenPrimaryPalette.fonts.lato,
        lineHeight: '1.4',
        letterSpacing: '0',
        fontWeight: '600',
        margin: '0 0 0.5rem 0',
      },
      '.header-h5': {
        variant: 'text.heading',
        fontSize: ['18px', '22px'],
        fontFamily: havenPrimaryPalette.fonts.lato,
        lineHeight: '1.5',
        fontWeight: '600',
        letterSpacing: '0',
        margin: '0 0 0.5rem 0',
      },
      '.header-h6': {
        variant: 'text.heading',
        fontSize: ['16px', '18px'],
        fontFamily: havenPrimaryPalette.fonts.lato,
        fontWeight: '400',
        lineHeight: '1.5',
        letterSpacing: '0',
        margin: '0 0 0.5rem 0',
        textTransform: 'none'
      },
      p: {
        lineHeight: ['1.6', '1.75'],
        fontSize: '16px',
        fontFamily: havenPrimaryPalette.fonts.lato,
        letterSpacing: '0',
        margin: 0,
        color: '#333333',
      },
      a: {
        color: 'link',
      },
      table: {
        borderCollapse: 'collapse',
        borderSpacing: 0,
      },
      tr: {
        fontSize: '1.1em',
      },
      th: {
        color: 'heading',
        padding: '0.4em',
        borderBottom: '2px solid rgba(0,0,0,0.2)',
        textTransform: 'uppercase',
        verticalAlign: 'middle',
      },
      td: {
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        padding: '0.4em',
        lineHeight: '1.8em',
        verticalAlign: 'middle',
        '& p': {
          fontSize: '80%',
          margin: '0.4em 0',
          lineHeight: '1.3',
        },
      },
    },
    textStyles: ['default', 'quote'],
    textColors: ['default', 'black', 'white', 'brown', 'darkBrown', 'taupe'],
    default: {
      h1: { 
        color: 'heading', 
      },
      h2: { 
        color: 'heading', 
      },
      h3: { 
        color: 'heading',
      },
      h4: { 
        color: 'heading',
      },
      h5: { 
        color: 'heading',
      },
      h6: { 
        color: 'heading'
      },
      p: {
        color: 'body'
      }
    },
    quote: {
      borderLeft: 'solid 4px #CBBAB0',
      padding: '10px 20px',
      color: '#333333',
      fontFamily: havenPrimaryPalette.fonts.lato,
      lineHeight: '1.5',
      fontStyle: 'normal',
    }
  },
};

export default havenStyles;